import { useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { AreaTag } from '../types/area-tags';
import { fetchAreaTags, fetchAreaTag, fetchAreaTagsByProject } from '../api/area-tags-api';

// Query keys for consistent caching
export const areaTagKeys = {
  all: ['area-tags'] as const,
  lists: () => [...areaTagKeys.all, 'list'] as const,
  list: (layoutId: string) => [...areaTagKeys.lists(), layoutId] as const,
  project: (projectId: string) => [...areaTagKeys.lists(), 'project', projectId] as const,
  details: () => [...areaTagKeys.all, 'detail'] as const,
  detail: (id: number, layoutId: string) => [...areaTagKeys.details(), id, layoutId] as const,
};

/**
 * Hook to fetch all area tags for a layout
 * @param layoutId - Layout ID to fetch area tags for
 * @param options - Additional query options
 * @returns Query result with area tags data
 */
export const useAreaTags = (
  layoutId: string,
  projectId: string,
  options?: Omit<UseQueryOptions<AreaTag[], Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: areaTagKeys.list(layoutId),
    queryFn: () => fetchAreaTags(layoutId, projectId),
    enabled: !!layoutId && !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      // Retry up to 3 times for network errors, but not for 4xx errors
      if (failureCount >= 3) return false;
      if (error.message.includes('4')) return false; // 400-499 errors
      return true;
    },
    ...options,
  });
};

/**
 * Hook to fetch a specific area tag by ID
 * @param areaTagId - Area tag ID to fetch
 * @param layoutId - Layout ID for the area tag
 * @param options - Additional query options
 * @returns Query result with area tag data
 */
export const useAreaTag = (
  areaTagId: number | null,
  layoutId: string,
  options?: Omit<UseQueryOptions<AreaTag, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: areaTagKeys.detail(areaTagId!, layoutId),
    queryFn: () => fetchAreaTag(areaTagId!, layoutId),
    enabled: !!areaTagId && !!layoutId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      if (failureCount >= 3) return false;
      if (error.message.includes('4')) return false;
      return true;
    },
    ...options,
  });
};

/**
 * Hook to fetch all area tags for a project (across all layouts)
 * @param projectId - Project ID to fetch area tags for
 * @param options - Additional query options
 * @returns Query result with area tags data
 */
export const useAreaTagsByProject = (
  projectId: string,
  options?: Omit<UseQueryOptions<AreaTag[], Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: areaTagKeys.project(projectId),
    queryFn: () => fetchAreaTagsByProject(projectId),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      // Retry up to 3 times for network errors, but not for 4xx errors
      if (failureCount >= 3) return false;
      if (error.message.includes('4')) return false; // 400-499 errors
      return true;
    },
    ...options,
  });
};

/**
 * Hook to get area tags with optimistic loading state
 * Provides cached data immediately if available, with loading states
 * @param layoutId - Layout ID to fetch area tags for
 * @param projectId - Project ID required for API call
 * @returns Enhanced query result with optimistic states
 */
export const useAreaTagsOptimistic = (layoutId: string, projectId: string) => {
  const query = useAreaTags(layoutId, projectId);

  return {
    ...query,
    // Provide immediate data from cache if available
    data: query.data || [],
    // Show loading only if we have no cached data
    isInitialLoading: query.isLoading && !query.data,
    // Show background loading when refetching with existing data
    isRefetching: query.isFetching && !!query.data,
    // Helper to check if we have any data (cached or fresh)
    hasData: !!query.data && query.data.length > 0,
  };
};

/**
 * Hook to prefetch area tags for a layout
 * Useful for preloading data before navigation
 * @param layoutId - Layout ID to prefetch area tags for
 * @param projectId - Project ID required for API call
 */
export const usePrefetchAreaTags = () => {
  const queryClient = useQueryClient();

  return (layoutId: string, projectId: string) => {
    if (!layoutId || !projectId) return;

    queryClient.prefetchQuery({
      queryKey: areaTagKeys.list(layoutId),
      queryFn: () => fetchAreaTags(layoutId, projectId),
      staleTime: 5 * 60 * 1000,
    });
  };
};

/**
 * Hook to invalidate area tags cache
 * Useful for forcing a refetch after external changes
 */
export const useInvalidateAreaTags = () => {
  const queryClient = useQueryClient();

  return {
    // Invalidate all area tags
    invalidateAll: () => {
      queryClient.invalidateQueries({ queryKey: areaTagKeys.all });
    },
    // Invalidate area tags for specific layout
    invalidateLayout: (layoutId: string) => {
      queryClient.invalidateQueries({ queryKey: areaTagKeys.list(layoutId) });
    },
    // Invalidate area tags for specific project
    invalidateProject: (projectId: string) => {
      queryClient.invalidateQueries({ queryKey: areaTagKeys.project(projectId) });
    },
    // Invalidate specific area tag
    invalidateAreaTag: (areaTagId: number, layoutId: string) => {
      queryClient.invalidateQueries({ queryKey: areaTagKeys.detail(areaTagId, layoutId) });
    },
  };
};

/**
 * Hook to get cached area tag data without triggering a fetch
 * @param layoutId - Layout ID
 * @returns Cached area tags data or undefined
 */
export const useCachedAreaTags = (layoutId: string): AreaTag[] | undefined => {
  const queryClient = useQueryClient();

  return queryClient.getQueryData(areaTagKeys.list(layoutId));
};

/**
 * Hook to set area tags data in cache
 * Useful for optimistic updates
 */
export const useSetAreaTagsCache = () => {
  const queryClient = useQueryClient();

  return {
    // Set area tags for a layout
    setAreaTags: (layoutId: string, data: AreaTag[]) => {
      queryClient.setQueryData(areaTagKeys.list(layoutId), data);
    },
    // Set specific area tag
    setAreaTag: (areaTagId: number, layoutId: string, data: AreaTag) => {
      queryClient.setQueryData(areaTagKeys.detail(areaTagId, layoutId), data);

      // Also update the area tag in the list cache
      queryClient.setQueryData(areaTagKeys.list(layoutId), (oldData: AreaTag[] | undefined) => {
        if (!oldData) return [data];

        const index = oldData.findIndex((tag) => tag.id === areaTagId);
        if (index >= 0) {
          const newData = [...oldData];
          newData[index] = data;
          return newData;
        } else {
          return [...oldData, data];
        }
      });
    },
    // Remove area tag from cache
    removeAreaTag: (areaTagId: number, layoutId: string) => {
      queryClient.removeQueries({ queryKey: areaTagKeys.detail(areaTagId, layoutId) });

      // Also remove from list cache
      queryClient.setQueryData(areaTagKeys.list(layoutId), (oldData: AreaTag[] | undefined) => {
        if (!oldData) return [];
        return oldData.filter((tag) => tag.id !== areaTagId);
      });
    },
  };
};
